import { Controller } from "@hotwired/stimulus"

/**
 * Deferred canvas signal decorations (prototype, weeks-app#223).
 *
 * When the canvas renders with ?defer_signals=1, chips carry
 * data-assigned-job-person-signal-status="pending" and a hidden, absolutely
 * positioned badge slot. This controller fetches the cross-plan conflict
 * findings once the canvas is on screen and decorates the chips in place.
 *
 * The placeholder sits in the same slot the synchronous badge occupies --
 * after the person's name, before their comment -- so a decorated chip is
 * indistinguishable from one rendered with signals inline. That is a
 * deliberate trade: revealing an inline badge settles the name slightly,
 * where the earlier absolutely-positioned corner slot could not, but a badge
 * in an unfamiliar place is the worse problem.
 *
 * ## Decorations survive the morph
 *
 * Mutations answer with a full-page refresh that morphs the body, and the
 * server HTML always says "pending" -- so every morph used to wipe the badges
 * and the re-fetch brought them back an HTTP round trip later. Every badge on
 * the canvas blinked out after every edit, and again on a plain refresh.
 *
 * The two guards below stop the morph touching a decorated chip at all
 * (turbo:before-morph-element and turbo:before-morph-attribute are cancelable;
 * turbo_custom_actions.js guards the modal frame the same way). The badge
 * simply stays where it is, and the fetch that still runs afterwards updates
 * only what genuinely changed.
 *
 * That inverts one thing about #decorate: with the morph no longer handing
 * back a neutral chip before every pass, applying has to be the WHOLE truth
 * each time -- absolute, not additive -- and a finding that disappeared has to
 * be cleared explicitly, because nothing else resets it any more.
 */

// The attributes on a chip's status span that a decoration owns. Everything
// else on that span -- the person's name, their comment, the colour classes --
// is server state, and must keep morphing.
const DECORATED_ATTRIBUTES = new Set([
  "data-assigned-job-person-signal-status",
  "data-assigned-job-person-problem",
  "data-assigned-job-person-hint",
  "data-assigned-job-person-implicated",
  "title"
])

// What the server renders for a chip with no finding. #decorate applies this
// to undo a decoration, so the reset and the decoration cannot drift apart:
// there is one code path, and this is its zero.
const UNDECORATED = {
  status: "pending",
  problem: false,
  hint: false,
  implicated: false,
  title: "",
  badge_label: "",
  conflicts_path: null,
  related_assignment_ids: []
}

export default class extends Controller {
  static values = { url: String }

  connect() {
    if (!this.hasUrlValue || !this.urlValue) return

    this.morphElementGuard = (event) => this.guardMorphedElement(event)
    this.morphAttributeGuard = (event) => this.guardMorphedAttribute(event)
    // Mutations refresh the canvas via Turbo morph. The guards keep the
    // decorated chips through it; this re-fetch is what reflects whatever the
    // mutation actually changed, and it now updates chips in place instead of
    // repopulating an emptied canvas.
    this.morphHandler = () => this.load()
    this.refreshHandler = () => this.load()

    document.addEventListener("turbo:before-morph-element", this.morphElementGuard)
    document.addEventListener("turbo:before-morph-attribute", this.morphAttributeGuard)
    document.addEventListener("turbo:morph", this.morphHandler)
    document.addEventListener("canvas-signals:refresh", this.refreshHandler)
    this.load()
  }

  disconnect() {
    if (this.morphElementGuard) document.removeEventListener("turbo:before-morph-element", this.morphElementGuard)
    if (this.morphAttributeGuard) document.removeEventListener("turbo:before-morph-attribute", this.morphAttributeGuard)
    if (this.morphHandler) document.removeEventListener("turbo:morph", this.morphHandler)
    if (this.refreshHandler) document.removeEventListener("canvas-signals:refresh", this.refreshHandler)
    this.abortController?.abort()
  }

  // Keep a decorated badge wrapper exactly as the last decoration left it:
  // href, title, related-assignment ids, revealed state, and the
  // assignment-conflicts controller instance attached to it. Skipping an
  // element skips its subtree, which is the point -- everything inside the
  // wrapper is ours, and none of it is in the server's markup.
  //
  // Only once it is actually decorated, though. An untouched placeholder holds
  // nothing worth keeping, and leaving it to morph normally means this guard
  // cannot outlive the deferred render that installed it.
  //
  // What the morph is offering matters as much as what it would overwrite. The
  // wrapper carries an id now so idiomorph pairs it with the next render's
  // wrapper -- but a guard that refuses every pairing would, the first time a
  // chip gains a planning-effect icon, refuse the icon itself and leave the
  // chip stale. So this only holds its ground against another wrapper; offered
  // anything else, it stands aside and lets the morph do its work, and the
  // fetch re-decorates what comes out.
  guardMorphedElement(event) {
    const element = event.target
    if (!(element instanceof Element)) return
    if (!element.hasAttribute("data-canvas-signal-badge-wrapper")) return

    const incoming = event.detail?.newElement
    if (!(incoming instanceof Element) || !incoming.hasAttribute("data-canvas-signal-badge-wrapper")) return

    const badge = element.querySelector("[data-canvas-signal-badge]")
    if (!badge || badge.hidden) return
    if (!this.element.contains(element)) return

    event.preventDefault()
  }

  // The status attributes live on the chip's label span, which also carries the
  // person's name and their comment -- so this cannot skip the element, only
  // the handful of attributes a decoration writes to it.
  //
  // Reading the current status is what scopes the guard: nothing else on the
  // page carries that attribute, "pending" means no decoration has been
  // applied over it yet, and a chip on another canvas is somebody else's.
  guardMorphedAttribute(event) {
    if (!DECORATED_ATTRIBUTES.has(event.detail?.attributeName)) return

    const element = event.target
    if (!(element instanceof Element)) return

    const status = element.getAttribute("data-assigned-job-person-signal-status")
    if (status === null || status === "pending") return
    if (!this.element.contains(element)) return

    event.preventDefault()
  }

  async load() {
    // Checked again here, not only in connect(). The morph handler is a
    // DOCUMENT listener, so it fires for morphs that replace this element with
    // one that carries no url -- an ?expand= render, which renders signals
    // synchronously and therefore omits the value entirely. Stimulus has not
    // necessarily disconnected us by then, and fetch("") means "this page":
    // the canvas URL, asked for as JSON, which renders the API jbuilder and
    // 500s. That is how this was found.
    if (!this.hasUrlValue || !this.urlValue) return

    // Rapid morphs (cable-ready canvas updates) can start overlapping loads;
    // without ordering, an older payload could resolve last and overwrite a
    // newer one. Each load supersedes the previous: the superseded request is
    // aborted, and a stale response that already left the server is dropped.
    this.loadSequence = (this.loadSequence || 0) + 1
    const sequence = this.loadSequence
    this.abortController?.abort()
    this.abortController = new AbortController()

    let data
    try {
      const response = await fetch(this.urlValue, {
        headers: { Accept: "application/json" },
        signal: this.abortController.signal
      })
      if (!response.ok) return
      data = await response.json()
    } catch {
      return
    }
    if (sequence !== this.loadSequence) return

    this.reconcile(data?.decorations || {})
    this.element.setAttribute("data-canvas-signals-loaded", "true")
  }

  // Decorations outlive the morph now, so a finding that has gone away has
  // nothing left to reset it -- the server-rendered "pending" chip that used to
  // do that never lands. Clearing is this controller's job.
  //
  // What is currently decorated is read from the DOM rather than remembered
  // from the last pass: a Turbo restoration visit paints a cached snapshot that
  // is ALREADY decorated into a freshly connected controller that has applied
  // nothing, and instance state would miss every badge in it.
  reconcile(decorations) {
    this.element.querySelectorAll("[data-canvas-signal-badge]:not([hidden])").forEach((badge) => {
      const chip = badge.closest("[data-assigned-person-id]")
      if (!chip) return
      if (decorations[chip.getAttribute("data-assigned-person-id")]) return

      this.decorate(chip, UNDECORATED, false)
    })

    Object.entries(decorations).forEach(([id, decoration]) => this.apply(id, decoration))
  }

  apply(id, decoration) {
    // The payload keys by the record id, which every chip carries in
    // data-assigned-person-id. Do NOT reconstruct the element's dom_id here:
    // it embeds the full model param key
    // (canvas_assignment_person_staffing_plans_slots_assigned_jobs_person_<id>),
    // and rebuilding it by hand is exactly the mistake that shipped badges
    // that never applied.
    const chip = this.element.querySelector(`[data-assigned-person-id="${CSS.escape(String(id))}"]`)
    if (!chip) return

    this.decorate(chip, decoration, true)
  }

  // Absolute, never additive. While every morph handed back a neutral chip,
  // this only ever had to add to one; now that decorations persist, a chip
  // going from problem to hint would keep the orange it was given the first
  // time unless every attribute is written on every pass.
  decorate(chip, decoration, revealed) {
    const statusSpan = chip.querySelector("[data-assigned-job-person-signal-status]")
    if (statusSpan) {
      statusSpan.setAttribute("data-assigned-job-person-signal-status", decoration.status || "")
      statusSpan.setAttribute("data-assigned-job-person-problem", decoration.problem ? "true" : "false")
      statusSpan.setAttribute("data-assigned-job-person-hint", decoration.hint ? "true" : "false")
      statusSpan.setAttribute("data-assigned-job-person-implicated", decoration.implicated ? "true" : "false")
      // Emptied rather than removed: an absent title inherits an ancestor's
      // tooltip, an empty one suppresses it, and the server renders it empty.
      statusSpan.setAttribute("title", decoration.title || "")
    }

    // The badge placeholder already sits in the same slot the synchronous
    // badge occupies, with the same markup -- fill it in and reveal it.
    const badge = chip.querySelector("[data-canvas-signal-badge]")
    if (!badge) return

    this.setOrRemove(badge, "href", decoration.conflicts_path)
    this.setOrRemove(badge, "title", decoration.title)
    this.setOrRemove(badge, "data-assigned-job-person-problem-indicator", decoration.problem ? "true" : null)
    this.setOrRemove(badge, "data-assigned-job-person-hint-indicator", decoration.implicated ? "true" : null)

    const problem = Boolean(decoration.problem)
    badge.classList.toggle("text-orange-500/85", problem)
    badge.classList.toggle("dark:text-orange-300/85", problem)
    badge.classList.toggle("text-slate-400", !problem)
    badge.classList.toggle("dark:text-slate-500", !problem)

    // The wrapper carries the assignment-conflicts controller, exactly as the
    // synchronous badge does; setting the value drives the hover highlight.
    const wrapper = badge.closest("[data-canvas-signal-badge-wrapper]")
    if (wrapper) {
      wrapper.setAttribute(
        "data-assignment-conflicts-related-assignment-ids-value",
        JSON.stringify(decoration.related_assignment_ids || [])
      )
    }

    const label = chip.querySelector("[data-canvas-signal-badge-label]")
    if (label) label.textContent = decoration.badge_label || ""

    // `hidden` is only honoured while nothing sets a display; the display
    // utility is part of revealing, never present before it.
    badge.classList.toggle("inline-flex", revealed)
    badge.hidden = !revealed
  }

  setOrRemove(element, attribute, value) {
    if (value) {
      element.setAttribute(attribute, value)
    } else {
      element.removeAttribute(attribute)
    }
  }
}
